import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/data';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pj4gub4qjhggaepva0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(errMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-6">关于我们</h1>
            <p className="mt-2 text-foreground-500 text-sm md:text-base">了解91暗指南的品牌故事与团队使命</p>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">品牌介绍</h2>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  91暗指南成立于2024年，是专注于<strong>91暗</strong>领域的信息聚合与内容分享平台。
                  我们的使命是帮助每一位用户快速、准确地了解91暗的全貌，
                  找到最符合需求的内容与入口资源。
                </p>
                <p className="mt-3 text-foreground-600 leading-relaxed">
                  团队由资深的内容编辑、技术专家和用户体验设计师组成，
                  致力于为用户提供高质量、高价值的91暗相关信息。
                  无论是91暗入门指导、资源推荐，还是入口地址更新、使用技巧分享，
                  我们都力求做到最全面、最及时。
                </p>
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-primary-50/50">
                    <div className="font-heading text-2xl font-bold text-primary-600">500+</div>
                    <div className="text-xs text-foreground-500 mt-1">优质文章</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-accent-50/50">
                    <div className="font-heading text-2xl font-bold text-accent-600">50万+</div>
                    <div className="text-xs text-foreground-500 mt-1">月访问量</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-secondary-50/50">
                    <div className="font-heading text-2xl font-bold text-secondary-600">99%</div>
                    <div className="text-xs text-foreground-500 mt-1">用户好评</div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20team%20workspace%20with%20warm%20cream%20and%20amber%20tones%2C%20clean%20professional%20environment%2C%20soft%20natural%20lighting%2C%20editorial%20style%2C%20knowledge%20and%20information%20sharing%20atmosphere&width=600&height=400&seq=about-brand&orientation=landscape"
                  alt="91暗指南团队"
                  className="w-full aspect-[3/2] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">用户口碑</h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">来自真实用户的使用评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, starI) => (
                      <i
                        key={starI}
                        className={`text-sm w-4 h-4 flex items-center justify-center ${
                          starI < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">联系我们</h2>
              <p className="mt-3 text-foreground-500 text-sm md:text-base">
                有任何问题或建议？请通过以下表单与我们取得联系
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg border border-background-200/70 p-6 md:p-8 space-y-5"
            >
              <div className="form-hp-container">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  消息内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的消息内容（最多500字）"
                />
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 font-medium text-sm rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                发送消息
              </button>

              {formStatus !== 'idle' && (
                <div
                  className={`p-4 rounded-lg text-sm ${
                    formStatus === 'success'
                      ? 'bg-secondary-50 text-secondary-700 border border-secondary-200'
                      : 'bg-accent-50 text-accent-700 border border-accent-200'
                  }`}
                >
                  {formMessage}
                </div>
              )}
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 text-center">
              <div>
                <div className="w-11 h-11 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-mail-line text-lg text-primary-600" />
                </div>
                <h3 className="text-sm font-medium text-foreground-800">邮箱</h3>
                <p className="text-xs text-foreground-500 mt-1">contact@zxymg.cn</p>
              </div>
              <div>
                <div className="w-11 h-11 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                  <i className="ri-time-line text-lg text-secondary-600" />
                </div>
                <h3 className="text-sm font-medium text-foreground-800">响应时间</h3>
                <p className="text-xs text-foreground-500 mt-1">工作日24小时内回复</p>
              </div>
              <div>
                <div className="w-11 h-11 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-chat-smile-2-line text-lg text-accent-600" />
                </div>
                <h3 className="text-sm font-medium text-foreground-800">在线客服</h3>
                <p className="text-xs text-foreground-500 mt-1">周一至周五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于91暗指南',
              description: '91暗指南致力于为用户提供91暗相关的最全面、最权威的信息指南与资源整合。',
              url: 'https://zxymg.cn/about',
              mainEntity: {
                '@type': 'Organization',
                name: '91暗指南',
                description: '专注于91暗领域的信息聚合与内容分享平台',
                url: 'https://zxymg.cn/',
                contactPoint: {
                  '@type': 'ContactPoint',
                  contactType: 'customer service',
                  email: 'contact@zxymg.cn',
                  availableLanguage: 'Chinese',
                },
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}